import "./index.css";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { AuthKitProvider } from "@workos-inc/authkit-react";
import { ConvexProviderWithAuth, ConvexReactClient, useConvexAuth } from "convex/react";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";

import { ApplicationUpdateRecovery } from "@/components/application-update-recovery";
import { OfflineInteractionBlocker } from "@/components/offline-interaction-blocker";
import { RouteErrorComponent } from "@/components/route-error-component";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/contexts/theme-context";
import { useAuthFromWorkOS } from "@/hooks";
import { PostHogAppProvider } from "@/lib/analytics";
import {
  AppVersionCoordinator,
  registerAppVersionCoordinator,
} from "@/lib/app-version-coordinator";
import { reportAppVersionEvent } from "@/lib/app-version-observability";
import { loadRuntimeConfig } from "@/lib/runtime-config";
import { initSentry } from "@/lib/sentry";
import { routeTree } from "./routeTree.gen";

initSentry();

const appVersionCoordinator = new AppVersionCoordinator({
  clientRelease: import.meta.env.VITE_BUILD_ID,
  report: reportAppVersionEvent,
});
registerAppVersionCoordinator(appVersionCoordinator);

// Create router with auth context
const router = createRouter({
  routeTree,
  defaultErrorComponent: RouteErrorComponent,
  context: {
    auth: undefined!, // Will be provided by InnerApp
  },
});

// Register router for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
  interface RouteContext {
    auth: {
      isAuthenticated: boolean;
      isLoading: boolean;
    };
  }
}

/**
 * Inner component that provides auth context to router.
 *
 * IMPORTANT: We use useConvexAuth() here, NOT useAuth() from AuthKit.
 *
 * Why this matters:
 * - useAuth() tells you if the browser has a WorkOS token (client-side only)
 * - useConvexAuth() tells you if Convex backend has VALIDATED the JWT
 *
 * There's a timing gap where AuthKit says "authenticated" but Convex hasn't
 * validated the token yet. Using useAuth() would allow users to reach protected
 * routes before Convex is ready, causing queries to fail with "Not authenticated".
 *
 * Additionally, if the JWT is misconfigured (e.g., missing 'aud' claim), useAuth()
 * would show the app UI while all Convex queries fail. useConvexAuth() ensures
 * we only show protected content after Convex confirms the JWT is valid.
 */
function InnerApp() {
  const { isAuthenticated, isLoading } = useConvexAuth();

  return (
    <RouterProvider
      router={router}
      context={{
        auth: {
          isAuthenticated,
          isLoading,
        },
      }}
    />
  );
}

// Token Storage: Use localStorage instead of cookies
//
// WorkOS AuthKit's `devMode` prop controls token storage:
// - devMode=false (default): Uses cookies on workos.com domain
// - devMode=true: Uses localStorage, tokens sent in request body
//
// We must use localStorage because we don't have WorkOS's custom cookie domain
// feature. Without it, cookies are set on workos.com (third-party domain), which
// modern browsers block, causing "Missing refresh token" errors after the
// 5-minute access token expires.
//
// TODO(#46): Switch to cookie storage when we upgrade to WorkOS custom cookie domain.
//            This will allow HTTP-only cookies on our domain (e.g., auth.decisionformula.com).
//            To switch: set USE_LOCAL_STORAGE_FOR_TOKENS = false and remove devMode prop.
//
// See: https://workos.com/docs/user-management/custom-cookie-domain
const USE_LOCAL_STORAGE_FOR_TOKENS = true;

// Always use dynamic redirect URI based on current origin
// This allows the app to work on any deployment (preview, production, localhost)
// No need for VITE_WORKOS_REDIRECT_URI env var - just add allowed URIs in WorkOS dashboard
const redirectUri = `${window.location.origin}/auth/callback`;

const rootElement = document.getElementById("root")!;
async function bootstrap() {
  const runtimeConfig = await loadRuntimeConfig();
  appVersionCoordinator.start(runtimeConfig.release);
  const convex = new ConvexReactClient(runtimeConfig.convexUrl);
  if (rootElement.innerHTML) return;

  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <ThemeProvider>
        <PostHogAppProvider>
          {/* `onRedirectCallback` is intentionally not passed: the `state.returnTo` that
              `login.tsx` sends with `signIn()` is consumed by our own `/auth/callback` route
              through its `validateSearch` (TanStack Router auto-parses the JSON `state`
              search param), not by AuthKit. The `state` plumbing is not dead code. */}
          <AuthKitProvider
            clientId={import.meta.env.VITE_WORKOS_CLIENT_ID}
            redirectUri={redirectUri}
            devMode={USE_LOCAL_STORAGE_FOR_TOKENS}
          >
            <ConvexProviderWithAuth client={convex} useAuth={useAuthFromWorkOS}>
              <InnerApp />
              <ApplicationUpdateRecovery coordinator={appVersionCoordinator} />
              <Toaster />
              <OfflineInteractionBlocker />
            </ConvexProviderWithAuth>
          </AuthKitProvider>
        </PostHogAppProvider>
      </ThemeProvider>
    </StrictMode>
  );
}

function renderBootstrapFailure(error: unknown) {
  // biome-ignore lint/suspicious/noConsole: Boot failure occurs before application telemetry is available.
  console.error("[bootstrap] Runtime configuration failed", error);
  rootElement.replaceChildren();
  const panel = document.createElement("main");
  panel.setAttribute("role", "alert");
  panel.style.cssText =
    "max-width:40rem;margin:10vh auto;padding:2rem;font-family:system-ui,sans-serif;line-height:1.5";
  const heading = document.createElement("h1");
  heading.textContent = "Application configuration unavailable";
  const message = document.createElement("p");
  message.textContent =
    "The application could not load its deployment configuration. Reload the page or contact support if the problem continues.";
  panel.append(heading, message);
  rootElement.append(panel);
}

void bootstrap().catch(renderBootstrapFailure);
